import { Button, Message, Modal, Typography, useMessagedAsyncFn } from "@payping/web-components";
import { ProfilePlanModel, userApis } from "@payping/api";
import { buildUrl } from "@payping/utilities-shared/url";
import { FC } from "react";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppSelector } from "@/utils/redux";
import { useGoftino } from "@/utils/goftino/hook";
import { URLS } from "@payping/utilities-shared/consts";
import styles from "../../../assets/scss/profile/Upgrade.module.scss";
import { ButtonType, ButtonVariant } from "./types";

interface Props {
  plan: ProfilePlanModel;
  loadingDetail?: boolean;
  retryGetPlanData: () => Promise<void>;
}

export const PlanButton: FC<Props> = (props) => {
  const { plan, loadingDetail, retryGetPlanData } = props;
  const { t } = useAppTranslation();
  const invoiceCode = useAppSelector((state) => state.user?.panelInfo?.invoiceCode);
  const currentPlan = useAppSelector((state) => state.user?.panelInfo?.currentPlan);
  const isPlanOnExpirationThreshold = useAppSelector((state) => state.user?.panelInfo?.isPlanOnExpirationThreshold);
  const canChangePlan = useAppSelector((state) => state.user?.panelInfo?.canChangePlan);
  const pendingPlanId = useAppSelector((state) => state.user?.panelInfo?.pendingPlan?.id);
  const [modal, contextHolder] = Modal.useModal();
  const { openChat } = useGoftino();

  const [{ loading: loadingUpgrade }, upgrade] = useMessagedAsyncFn(
    async (planId?: number) => {
      const { code } = await userApis.setPanelWithPlanIdWithCancellationTokenPut(planId);
      await retryGetPlanData();
      if (plan.discountedPrice && plan.discountedPrice > 0) {
        const w = window.open("", "MyWindow") as Window;
        w.location.href = buildUrl(URLS.PPNG_URL, "v", code || "").toString();
      } else {
        Message.success({ content: t("upgrade.successMessage") });
      }
    },
    [plan, retryGetPlanData],
    undefined,
    { useProgress: true },
  );

  const loading = loadingDetail || loadingUpgrade;

  const allButtons: Record<ButtonVariant, ButtonType> = {
    change: {
      id: 1,
      label: plan.discountedPrice && plan.discountedPrice > 0 ? t("upgrade.upgradeToThisPlan") : t("upgrade.activate"),
      loading,
      onClick: (selectedPlan: number) => {
        modal.confirm({
          centered: true,
          content: (
            <>
              <Typography.Paragraph>{t("upgrade.newInvoiceMessage")}</Typography.Paragraph>
              <Typography.Text variant={"caption"}>* {t("upgrade.refundNotice")}</Typography.Text>
            </>
          ),
          maskClosable: true,
          okText: plan.discountedPrice && plan.discountedPrice > 0 ? t("upgrade.newInvoice") : undefined,
          onOk: () => upgrade(selectedPlan),
          title: t("upgrade.upgradeConfirm"),
        });
      },
      type: "primary",
    },
    current: {
      disabled: true,
      ghost: true,
      id: 2,
      label: t("upgrade.currentPlan"),
      type: "primary",
    },
    extend: {
      id: 3,
      label: t("upgrade.extendPlan"),
      loading,
      onClick: (selectedPlan: number) => {
        modal.confirm({
          centered: true,
          content: t("upgrade.extendMessage"),
          maskClosable: true,
          okText: t("upgrade.newInvoice"),
          onOk: () => upgrade(selectedPlan),
          title: t("upgrade.extendPlan"),
        });
      },
      type: "primary",
    },
    inactive: {
      disabled: true,
      id: 4,
      label: t("upgrade.confirmAndActive"),
    },
    pending: {
      id: 5,
      label: t("upgrade.waitingForPayment"),
      onClick: () => {
        window.open(buildUrl(URLS.PPNG_URL, "v", invoiceCode || "").toString(), "_blank");
      },
    },
    premium: {
      ghost: true,
      id: 6,
      label: t("upgrade.plans.agreement.contactUs"),
      onClick: () => openChat(),
    },
  };

  const getButton = (): ButtonType => {
    let button: ButtonType = allButtons.change;
    if (plan.isDefaultPlan) button = allButtons.inactive;

    if (currentPlan?.id === plan.id) {
      button = allButtons.current;

      if (isPlanOnExpirationThreshold) {
        button = allButtons.extend;
      }
    } else if (pendingPlanId === plan.id) {
      button = allButtons.pending;
    } else if (
      // If user's plan is byAgreement, he can't upgrade it
      currentPlan?.id !== 5 &&
      canChangePlan &&
      plan.discountedPrice &&
      currentPlan?.discountedPrice &&
      plan.discountedPrice > currentPlan?.discountedPrice
    ) {
      button = allButtons.change;
    }

    // Premium plan
    if (plan?.id === 5) {
      button = allButtons.premium;
    }
    return button;
  };
  const planButton = getButton();

  return (
    <>
      <Button
        data-google-track={`${plan.englishTitle}-plan-button`}
        disabled={planButton.disabled}
        loading={planButton.loading}
        size={"large"}
        type={planButton.type}
        ghost={planButton.ghost}
        className={styles.planButton}
        onClick={() => planButton.onClick?.(plan.id || 0)}
        block
      >
        {planButton.label}
      </Button>
      {contextHolder}
    </>
  );
};
